import qs from 'qs';
import { RestApiError } from '../utils';
import { auth } from './endpoints';
import { HttpMethodEnum, type IRequest, type IRequestParams } from '../models';
import { LocalStorageUtils } from '@shared/utils';
import { useConfirmLoginStore, useUserStore } from '@lib/store';

export class Api {
  private static readonly baseUrl = process.env.API_URL;

  // private static accessToken: string | null = null;
  private static refreshToken: string | null = null;
  private static isRefreshing = false;
  private static refreshPromise: Promise<void> | null = null;

  // static setTokens = (tokens: { accessToken: string; refreshToken: string }) => {
  //   this.accessToken = tokens.accessToken;
  //   this.refreshToken = tokens.refreshToken;
  // };

  private static _objectToQuery = (obj?: object) => {
    if (!obj) return '';
    return qs.stringify(obj, { addQueryPrefix: true, indices: false, allowDots: true, skipNulls: true });
  };

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<void, Response>({
        method: HttpMethodEnum.Get,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        responseType: request.responseType,
        responseErrorType: request.responseErrorType,
        signal: request.signal,
      }),
    );
  }

  protected static post<Request, Response = unknown>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Post,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        data: request.data,
        responseType: request.responseType,
        responseErrorType: request.responseErrorType,
        signal: request.signal,
      }),
    );
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Put,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        data: request.data,
        responseType: request.responseType,
        responseErrorType: request.responseErrorType,
        signal: request.signal,
      }),
    );
  }

  protected static patch<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Patch,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        data: request.data,
        responseType: request.responseType,
        responseErrorType: request.responseErrorType,
        signal: request.signal,
      }),
    );
  }

  protected static delete<Request, Response = unknown>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Delete,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        data: request.data,
        responseType: request.responseType,
        responseErrorType: request.responseErrorType,
        signal: request.signal,
      }),
    );
  }

  private static async _wrapRequest<Response>(handler: () => Promise<Response>): Promise<Response> {
    try {
      return await handler();
    } catch (error: unknown) {
      if (error instanceof RestApiError && error.status === 401 && this.refreshToken) {
        await this._refreshTokens();
        return handler();
      }
      throw error;
    }
  }

  private static async _refreshTokens() {
    if (this.isRefreshing) return this.refreshPromise!;

    this.isRefreshing = true;
    this.refreshPromise = fetch(this.baseUrl + auth.refreshToken(), { method: 'POST', headers: { Authorization: `Bearer ${this.refreshToken}` } })
      .then((res) => {
        if (!res.ok) throw new RestApiError('Token refresh failed', 401, {}, 'GET', auth.refreshToken());
        return res.json();
      })
      .then((data) => {
        // this.accessToken = data.accessToken;
        this.refreshToken = data.refreshToken;
      })
      .finally(() => {
        this.isRefreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private static async _request<Request, Response>(request: IRequest<Request>): Promise<Response> {
    const requestId = crypto.randomUUID();

    const headers: Record<string, string> =
      request?.data instanceof FormData
        ? { ...request.headers }
        : {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Request-Id': requestId,
            ...request.headers,
          };
    const user = LocalStorageUtils.getUser();
    if (!headers.Authorization && headers.Authorization !== '') {
      if (user) headers.Authorization = `Bearer ${user.accessToken}`;
    }

    const options = {
      method: request.method || 'GET',
      headers,
      body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
      signal: request.signal,
    };

    const response = await fetch(this.baseUrl + request.url, options);

    if (response.status === 401 && user?.refreshToken) {
      LocalStorageUtils.removeUser();
      useUserStore.getState().clear;
      useConfirmLoginStore.getState().clear();
      window.location.reload();
      console.log();
    }

    if (response.ok) {
      if (response.status === 204 || response.headers.get('Content-Length') === '0') return undefined as Response;
      const contentType = response.headers.get('content-type');
      if (request.responseType === 'none') return undefined as Response;
      if (contentType?.indexOf('application/json') !== -1) return response.json();
      return response.blob() as Response;
    } else {
      let error;
      let message = '';

      if (request.responseErrorType === 'none') {
        error = new RestApiError('', response.status, {}, request.method, request.url);
      } else {
        const data = await response.json();
        message = Array.isArray(data) ? data.join(' ') : data?.message;
        error = new RestApiError(message, response.status, data, request.method, request.url);
      }

      throw error;
    }
  }
}
